import { AxiosInstance, isAxiosError } from "axios";
import * as requestUrl from "../../constants/request";

import { AuthSession, UserAccount } from "./user";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AccountQueryParameter } from "store/account/account-state";
import { extractError } from "utility";
import { ThunkAPIConfig } from "store";
import { addNotification } from "store/notification/notification-utils";

export type FetchUserAccount = typeof fetchUserAccount;

export const fetchUserAccount = createAsyncThunk<
  UserAccount,
  {
    request: AxiosInstance;
  },
  ThunkAPIConfig
>("user/fetchUserAccount", async ({ request }) => {
  const response = await request(requestUrl.FT_USERS);
  return response.data;
});

export type CreateUserAccount = typeof createUserAccount;

export const createUserAccount = createAsyncThunk<
  string,
  {
    request: AxiosInstance;
    data: {
      user: UserAccount;
      authSession: AuthSession;
    };
  },
  ThunkAPIConfig
>(
  "user/createUserAccount",
  async ({ request, data: { authSession, user } }, thunkapi) => {
    let enterActionId: number | null = null;
    try {
      if (!!(window as any).dtrum) {
        enterActionId = (window as any).dtrum.enterAction(
          "send-account"
        ) as number;
      }
      const response = await request(requestUrl.FT_USERS_CREATE, {
        data: {
          token: authSession.session_token,
          idpState: authSession.state,
          userAccountInformation: {
            ...user,
          },
        },
        method: "POST",
      });
      if (!!enterActionId && !!(window as any).dtrum) {
        (window as any).dtrum.leaveAction(enterActionId);
      }
      return response.data;
    } catch (err: any) {
      if (!!(window as any).dtrum) {
        (window as any).dtrum.reportError(err, enterActionId);
      }
      let err_key = "no_data_found";
      const { isSlideOpen } = thunkapi.getState().common;
      addNotification(thunkapi.dispatch, isSlideOpen, {
        messages: [err_key],
        type: "ERROR",
        closable: true,
      });
      return thunkapi.rejectWithValue({
        response: {
          status: 401,
        },
      });
    }
  }
);

export type AttachAccount = typeof attachAccount;

export const attachAccount = createAsyncThunk<
  boolean,
  {
    request: AxiosInstance;
    data: {
      accountOwnershipIdentifiers: AccountQueryParameter[];
      authSession: AuthSession;
    };
  },
  ThunkAPIConfig
>(
  "user/attachAccount",
  async (
    { request, data: { accountOwnershipIdentifiers, authSession } },
    thunkapi
  ) => {
    let attachAccountActionId: number | null = null;
    try {
      if (!!(window as any).dtrum) {
        attachAccountActionId = (window as any).dtrum.enterAction(
          "attach-account"
        ) as number;
      }
      const response = await request(requestUrl.FT_ACCOUNT_ATTACH, {
        data: {
          token: authSession.session_token,
          accountOwnershipIdentifiers,
        },
        method: "POST",
      });

      if (!!attachAccountActionId && !!(window as any).dtrum) {
        (window as any).dtrum.leaveAction(attachAccountActionId);
      }
      return response.data;
    } catch (err: any) {
      if (!!(window as any).dtrum) {
        (window as any).dtrum.reportError(err, attachAccountActionId);
      }

      let errMsg = "no_data_found";
      if (err.response?.status === 404) {
        errMsg = "invalid_identifier_number";
      }

      if (err.response?.status === 401) {
        errMsg = "registration_session_expired";
      }
      const { isSlideOpen } = thunkapi.getState().common;
      addNotification(thunkapi.dispatch, isSlideOpen, {
        messages: [errMsg],
        type: "ERROR",
        closable: true,
      });
      if (isAxiosError(err)) {
        return thunkapi.rejectWithValue({
          response: {
            status: err.response?.status,
          },
        });
      } else {
        return thunkapi.rejectWithValue({
          response: {
            status: 401,
          },
        });
      }
    }
  }
);

export type UpdateUserAccount = typeof updateUserAccount;

export const updateUserAccount = createAsyncThunk<
  UserAccount,
  {
    request: AxiosInstance;
    data: UserAccount;
  },
  ThunkAPIConfig
>("user/updateUserAccount", async ({ request, data: user }, thunkapi) => {
  let enterActionId: number | null = null;
  try {
    if (!!(window as any).dtrum) {
      enterActionId = (window as any).dtrum.enterAction(
        "update-account"
      ) as number;
    }
    const response = await request(requestUrl.FT_USERS_CREATE, {
      data: {
        ...user,
        emailAddress: user.email,
      },
      method: "PUT",
    });
    if (!!enterActionId && !!(window as any).dtrum) {
      (window as any).dtrum.leaveAction(enterActionId);
    }
    return response.data;
  } catch (err: any) {
    if (!!(window as any).dtrum) {
      (window as any).dtrum.reportError(err, enterActionId);
    }
    const { isSlideOpen } = thunkapi.getState().common;

    if (err.response.data === "auth0_idp_error") {
      addNotification(thunkapi.dispatch, isSlideOpen, {
        messages: ["email_in_use"],
        type: "ERROR",
        closable: true,
      });
    } else {
      const errKey = extractError(err);
      addNotification(thunkapi.dispatch, isSlideOpen, {
        messages: errKey,
        type: "ERROR",
        closable: true,
      });
    }

    return thunkapi.rejectWithValue(null);
  }
});
